import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";
import "./Facturen.css";

const Facturen = () => {
  const { company } = useCompanyContext();
  const { t } = useCompanyContext();
  const { cultivationCompany } = useCultivationCompanyContext();
  const [loadingInvoice, setLoadingInvoice] = useState(null);

  const data = company?.invoices || [];
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([{ id: "Posting_Date", desc: true }]);

  const columns = [
    {
      accessorKey: "Posting_Date",
      header: t.datum,
      meta: { className: "col-datum" },
      cell: (info) => {
        const dateValue = info.getValue();
        return dateValue
          ? new Date(dateValue).toLocaleDateString("nl-NL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "N.v.t.";
      },
    },
    {
      accessorKey: "No",
      header: t.factuur,
      meta: { className: "col-factuur" },
    },
    {
      accessorKey: "Bill_to_Name",
      header: t.naam,
      meta: { className: "col-naam" },
    },
    {
      accessorKey: "Posting_Description",
      header: t.omschrijving,
      meta: { className: "col-omschrijving", width: "25%" },
    },
    {
      accessorKey: "Due_Date",
      header: t.vervaldatum,
      meta: { className: "col-vervaldatum" },
      cell: (info) => {
        const dateValue = info.getValue();
        return dateValue
          ? new Date(dateValue).toLocaleDateString("nl-NL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "N.v.t.";
      },
    },
    {
      accessorKey: "Closed",
      header: t.status,
      meta: { className: "col-status" },
      cell: (info) => (info.getValue() ? "✅" : "❌"),
    },
    {
      accessorKey: "Amount",
      header: t.exclBtw,
      meta: { className: "col-exbtw" },
      cell: (info) =>
        info.getValue()
          ? `€ ${info.getValue().toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
            })}`
          : "N.v.t.",
    },
    {
      accessorKey: "Amount_Including_VAT",
      header: t.inclBtw,
      meta: { className: "col-incbtw" },
      cell: (info) =>
        info.getValue()
          ? `€ ${info.getValue().toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
            })}`
          : "N.v.t.",
    },
    {
      accessorKey: "Your_Reference",
      header: t.ordernummer,
      meta: { className: "col-order" },
    },

    ...(cultivationCompany?.name === "WPK"
      ? [
          {
            header: t.eindbon,
            meta: { className: "col-acties" },
            cell: ({ row }) =>
              row.original.Your_Reference ? (
                <button
                  className="download-button"
                  onClick={() =>
                    handleInvoiceClickEindbon(row.original.Your_Reference)
                  }
                  disabled={loadingInvoice === row.original.No}
                >
                  {loadingInvoice === row.original.No ? "Bezig..." : t.eindbon}
                </button>
              ) : null,
          },
          {
            header: t.facturen,
            meta: { className: "col-acties" },
            cell: ({ row }) => (
              <button
                className="download-button"
                onClick={() =>
                  handleInvoiceClickGespfactuur(row.original.No)
                }
                disabled={loadingInvoice === row.original.No}
              >
                {loadingInvoice === row.original.No ? "Bezig..." : t.factuur}
              </button>
            ),
          },
        ]
      : []),

    ...(cultivationCompany?.name === "P. van Geest"
      ? [
          {
            header: t.facturen,
            meta: { className: "col-acties" },
            cell: ({ row }) => (
              <button
                className="download-button"
                onClick={() =>
                  handleInvoiceClickFactuur(row.original.No)
                }
                disabled={loadingInvoice === row.original.No}
              >
                {loadingInvoice === row.original.No ? "Bezig..." : t.factuur}
              </button>
            ),
          },
        ]
      : []),
  ];

  /* download functies (ongewijzigd) */
  const handleInvoiceClickGespfactuur = async (invoiceNo) => {
    setLoadingInvoice(invoiceNo);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/invoices/download/specifiedinvoice/${invoiceNo}`,
        { headers: { "X-Cultivation-Company": cultivationCompany.name } }
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNo}.pdf`;
      a.click();
    } finally {
      setLoadingInvoice(null);
    }
  };

  const handleInvoiceClickFactuur = async (invoiceNo) => {
    setLoadingInvoice(invoiceNo);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/invoices/download/invoice/${invoiceNo}`,
        { headers: { "X-Cultivation-Company": cultivationCompany.name } }
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNo}.pdf`;
      a.click();
    } finally {
      setLoadingInvoice(null);
    }
  };

  const handleInvoiceClickEindbon = async (invoiceNo) => {
    setLoadingInvoice(invoiceNo);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/invoices/download/shipmentendnotification/${invoiceNo}`,
        { headers: { "X-Cultivation-Company": cultivationCompany.name } }
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNo}.pdf`;
      a.click();
    } finally {
      setLoadingInvoice(null);
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId);
      const searchTerm = String(filterValue ?? "").trim().toLowerCase();
    
      if (!searchTerm) return true;
    
      // Bestaande zoekfunctionaliteit behouden
      const matchesOriginal = String(value ?? "")
        .toLowerCase()
        .includes(searchTerm);
    
      if (matchesOriginal) return true;
    
      // Zoeken op Nederlandse datumnotatie
      // Zowel factuurdatum als vervaldatum
      if (
        (columnId === "Posting_Date" || columnId === "Due_Date") &&
        value
      ) {
        const date = new Date(value);
    
        if (!Number.isNaN(date.getTime())) {
          const formattedDate = date.toLocaleDateString("nl-NL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
    
          return formattedDate.includes(searchTerm);
        }
      }
    
      return false;
    },
    initialState: { pagination: { pageSize: 10 } },
  });

  return (
    <div className="invoice-table">
      <h1 className="pageheading">{t.facturen}</h1>
      <div className="search-container">
      <input
        className="search-input"
        placeholder={t.zoekPlaceholderFacturen}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      </div>
      <table>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className={h.column.columnDef.meta?.className}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cell.column.columnDef.meta?.className}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {t.pagination.previous}
        </button>
        <span>
        {t.pagination.page}{" "}
          <strong>
            {table.getState().pagination.pageIndex + 1} {t.pagination.of}{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        {t.pagination.next}
        </button>
      </div>

      <style>
        {`
          @media (max-width: 1100px) {
            .col-omschrijving,
            .col-order {
              display: none;
            }
          }

          @media (max-width: 900px) {
            .col-naam,
            .col-incbtw {
              display: none;
            }
          }

          @media (max-width: 700px) {
            .col-vervaldatum {
              display: none;
            }

            table th,
            table td {
              font-size: 13px;
              padding: 6px 8px;
            }
          }

          .invoice-table {
            overflow-x: auto;
          }
        `}
      </style>
    </div>
  );
};

export default Facturen;
